import { IERPUser, IFacility, IVendor } from '@admin-panel/types';
import { IOrganization } from '@admin-panel/types/lib/org';
import { useGetIdentity, useOne } from '@refinedev/core';
import { Flex, Spin } from 'antd';
import { createContext, useContext, PropsWithChildren, useEffect, useState } from 'react';
import {
  getAdminVendorFacilityId,
  getAdminVendorId,
  setAdminVendorFacilityId,
  setAdminVendorId,
} from '@admin-panel/providers';

interface VendorGlobalContextType {
  organization: IOrganization;
  vendor?: IVendor;
  setVendor: (vendor: IVendor | undefined) => void;
  vendorFacilityId?: number;
  vendorFacility?: IFacility;
  setVendorFacility: (vendorFacility: IFacility | undefined) => void;
}

const VendorGlobalContext = createContext<VendorGlobalContextType | undefined>(undefined);

const isNexusSuperAdmin = (user?: IERPUser) =>
  Boolean(user?.roles?.some(role => role.name === 'NEXUS_SUPER_ADMIN'));

export const VendorGlobalProvider = ({ children }: PropsWithChildren) => {
  const { data: user } = useGetIdentity<IERPUser>();
  const [vendor, setVendor] = useState<IVendor | undefined>();
  const [vendorFacility, setVendorFacility] = useState<IFacility | undefined>();

  const isNexusAdmin = Boolean(user && isNexusSuperAdmin(user));
  const adminVendorId = isNexusAdmin ? getAdminVendorId() : undefined;
  const parsedAdminVendorId = adminVendorId ? Number(adminVendorId) : undefined;
  const normalizedAdminVendorId = Number.isNaN(parsedAdminVendorId)
    ? undefined
    : parsedAdminVendorId;
  const vendorLookupId = isNexusAdmin
    ? normalizedAdminVendorId
    : user?.userType === 'vendor'
      ? user?.vendorId
      : undefined;

  const {
    result: vendorResp,
    query: { isLoading: vendorFetchInProgress },
  } = useOne<IVendor>({
    resource: 'vendors',
    id: vendorLookupId,
    queryOptions: {
      enabled: !!vendorLookupId,
    },
  });

  const adminVendorFacilityId = isNexusAdmin ? getAdminVendorFacilityId() : undefined;
  const adminVendorFacility =
    adminVendorFacilityId && vendorResp?.facilities
      ? vendorResp.facilities.find(facility => facility.id === adminVendorFacilityId)
      : undefined;

  const resolvedVendor = vendor ?? vendorResp;
  const resolvedVendorFacility =
    vendorFacility ?? (isNexusAdmin ? adminVendorFacility : vendorResp?.facilities?.[0]);
  const resolvedVendorFacilityId = resolvedVendorFacility?.id;

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!isNexusSuperAdmin(user)) {
      setAdminVendorId();
      setAdminVendorFacilityId();
    }
  }, [user]);

  const {
    result: orgResp,
    query: { isLoading: orgFetchInProgress },
  } = useOne<IOrganization>({
    resource: 'org',
    id: 'lyskraft',
  });

  if (orgFetchInProgress || vendorFetchInProgress) {
    return (
      <Flex
        vertical
        style={{
          width: '100%',
          height: 500,
        }}
        justify="space-around"
        align="center"
      >
        <Spin tip="Loading" spinning size="large" />
      </Flex>
    );
  }

  if (!orgResp) {
    return <div>You are not allowerd to access this page</div>;
  }

  return (
    <VendorGlobalContext.Provider
      value={{
        organization: orgResp,
        vendor: resolvedVendor,
        setVendor: (nextVendor: IVendor | undefined) => {
          setVendor(prevVendor => {
            if (prevVendor?.id !== nextVendor?.id) {
              setVendorFacility(undefined);
              if (isNexusSuperAdmin(user)) {
                setAdminVendorFacilityId();
              }
            }
            return prevVendor?.id === nextVendor?.id ? prevVendor : nextVendor;
          });
          if (isNexusSuperAdmin(user)) {
            setAdminVendorId(nextVendor?.id);
          }
        },
        vendorFacilityId: resolvedVendorFacilityId,
        vendorFacility: resolvedVendorFacility,
        setVendorFacility: (facility?: IFacility) => {
          setVendorFacility(facility);
          if (isNexusSuperAdmin(user)) {
            setAdminVendorFacilityId(facility?.id);
          }
        },
      }}
    >
      {children}
    </VendorGlobalContext.Provider>
  );
};

export const useVendorGlobalContext = () => {
  const context = useContext(VendorGlobalContext);
  if (context === undefined) {
    throw new Error('useVendorGlobalContext must be used within a VendorGlobalProvider');
  }
  return context;
};

export const useOptionalVendorGlobalContext = () => useContext(VendorGlobalContext);
